import React, { PropTypes, Component } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { PageType, ContentType, ContentStatus } from 'enums';
import { withRouter } from 'react-router';
import injectIntl, { intlShape } from 'common/injectIntl';
import { getSearchResults, SEARCH_TYPES } from 'common/actions/apiAction';
import SearchSuggestionGroup from './SearchSuggestionGroup';
import { competitorSelector } from 'common/selectors/pageSelector';
import { apiEnvSelector } from 'common/selectors/appSelector';
import { trackSearch } from 'lib/omniture';

class SearchComponent extends Component {
    constructor(props) {
        super(props);
        this.handleSearchSubmit = this.handleSearchSubmit.bind(this);
        this.handleSearchTermChange = this.handleSearchTermChange.bind(this);
        this.handleInputOnBlur = this.handleInputOnBlur.bind(this);
        this.handleMouseEnter = this.handleMouseEnter.bind(this);
        this.handleMouseLeave = this.handleMouseLeave.bind(this);
        this.handleArrowKeyDown = this.handleArrowKeyDown.bind(this);
        this.handleSuggestionSelected = this.handleSuggestionSelected.bind(this);
        this.routeToSearchTerm = this.routeToSearchTerm.bind(this);
        this.setStateSearchTerm = this.setStateSearchTerm.bind(this);
        this.handleFooterClick = this.handleFooterClick.bind(this);
        this.handleMouseEnterItem = this.handleMouseEnterItem.bind(this);
        this.handleMouseLeaveItem = this.handleMouseLeaveItem.bind(this);

        this.state = {
            searchTerm: '',
            userEnteredText: '',
            suggestionBuckets: [],
            canCollapse: true,
            displaySuggestions: false,
            selectedIndex: -1,
            suggestionCount: 0,
            enterPressed: false,
            showFooter: true
        };
    }

    componentDidMount() {
        document.addEventListener('keydown', this.handleArrowKeyDown);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleArrowKeyDown);
    }

    setStateSearchTerm(term) {
        this.setState({ searchTerm: term, suggestionBuckets: [], displaySuggestions: false });
    }

    handleSuggestionSelected({ type, name, status }, enterPressed = false) {
        const { pageType, entitlementsTracking } = this.props;
        const fromHero = (pageType !== PageType.SEARCH);
        if ((type === ContentType.VOD) ||
            (type === ContentType.LISTING && (status === ContentStatus.REPLAY || status === ContentStatus.LIVE))) {
            const placement = pageType === PageType.SEARCH ? 'search-page' : 'search-hero';
            trackSearch({
                type,
                status,
                isSuggestion: true,
                fromHero,
                searchTerm: name,
                entitlementsTracking
            });
            this.setState({ placement, userInitiated: true, enterPressed });
            return;
        }
        trackSearch({
            isSuggestion: true,
            fromHero,
            searchTerm: name,
            entitlementsTracking
        });
        this.routeToSearchTerm(name);
    }

    handleMouseEnter() {
        this.setState({ canCollapse: false });
    }

    handleMouseLeave() {
        this.setState({ canCollapse: true });
    }

    handleFooterClick() {
        const searchTerm = this.state.userEnteredText;
        trackSearch({
            isSuggestion: true,
            fromHero: (this.props.pageType !== PageType.SEARCH),
            searchTerm,
            isViewAllResults: true,
            entitlementsTracking: this.props.entitlementsTracking
        });
        this.routeToSearchTerm(searchTerm);
    }

    handleMouseEnterItem(index) {
        this.setState({ searchTerm: this.state.userEnteredText, selectedIndex: index });
    }

    handleMouseLeaveItem() {
        this.setState({ selectedIndex: -1 });
    }

    routeToSearchTerm(term) {
        this.setStateSearchTerm(term);
        if (this.props.pageType === PageType.SID) {
            this.props.router.push(`/sid/${encodeURIComponent(term)}`);
        } else {
            this.props.router.push(`/search/${encodeURIComponent(term)}`);
        }
    }

    handleSearchSubmit(event) {
        event.preventDefault();
        if (this.state.selectedIndex !== -1) {
            const contents = this.state.suggestionBuckets
                .map(suggestion => suggestion.contents)
                .reduce((cur, result) => [...cur, ...result], []);
            const content = contents[this.state.selectedIndex];
            this.handleSuggestionSelected(content, true);
            return;
        }
        if (this.state.searchTerm) {
            trackSearch({
                fromHero: (this.props.pageType !== PageType.SEARCH),
                searchTerm: this.state.searchTerm,
                entitlementsTracking: this.props.entitlementsTracking
            });
            this.routeToSearchTerm(this.state.searchTerm);
        }
    }

    handleArrowKeyDown(event) {
        const { keyCode } = event;
        if (!this.state.displaySuggestions || (keyCode !== 38 && keyCode !== 40)) {
            return;
        }
        event.preventDefault();
        const contents = this.state.suggestionBuckets
            .map(suggestion => suggestion.contents)
            .reduce((cur, result) => [...cur, ...result], []);
        // 38 is UP
        if (keyCode === 38) {
            const newIndex = Math.max(this.state.selectedIndex - 1, 0);
            const content = contents[newIndex];
            this.setState({ selectedIndex: newIndex, searchTerm: content.suggestion });
        } else if (keyCode === 40) {
            const newIndex = Math.min(this.state.suggestionCount - 1, this.state.selectedIndex + 1);
            const content = contents[newIndex];
            this.setState({ selectedIndex: newIndex, searchTerm: content.suggestion });
        }
    }

    handleSearchTermChange(event) {
        const searchTermQuery = event.target.value;
        // Updating the searchTerm to be used while searching by pressing 'enter' key,
        // and resetting suggestions to 0 for rendering
        // if somebody backspacspaces in the textbox
        this.setState({ userEnteredText: searchTermQuery, searchTerm: searchTermQuery, selectedIndex: -1 });

        // Suggestions are only returned when the query is 3 char or more long
        if (this.props.pageType === PageType.SID) {
            const typeAhead = this.props.typeAhead;
            const teamSearchQuery = searchTermQuery.toLowerCase();
            const contents = typeAhead
                .filter((term) => term.toLowerCase().indexOf(teamSearchQuery) !== -1)
                .map((term, index) => ({ name: term, id: index }));
            const sugBuckets = [{ name: 'Teams', type: 'terms', contents }];
            this.setState({ suggestionBuckets: sugBuckets, displaySuggestions: true, suggestionCount: typeAhead.length });
        } else if (searchTermQuery.length > 2) {
            const { dispatch, apiEnv } = this.props;
            dispatch(getSearchResults(searchTermQuery, apiEnv, SEARCH_TYPES.suggest)).then(({ page: { buckets = [] } }) => {
                const count = buckets.reduce((sum, bucket) => (sum + bucket.contents.length), 0);
                this.setState({ suggestionBuckets: buckets, displaySuggestions: true, suggestionCount: count });
            }).catch(() => {});
        } else {
            this.setState({ displaySuggestions: false, suggestionBuckets: [] });
        }
    }

    handleInputOnBlur() {
        if (this.state.canCollapse) {
            this.setState({ displaySuggestions: false });
        }
    }

    render() {
        const { enabled, messages, intl: { formatMessage }, pageType } = this.props;
        const searchClasses = classNames('search-section', 'icon-font-before', 'icon-search-solid-before');
        const style = pageType === PageType.SEARCH ?
                    { width: '100%', margin: '0 auto 16px' }
                    : { width: '100%', margin: '0 auto' };
        const suggestions = this.state.suggestionBuckets;
        let offset = 0;
        const suggestionElements = suggestions.map((suggestion) => {
            const element = (
                <SearchSuggestionGroup
                    offset={offset}
                    selectedIndex={this.state.selectedIndex}
                    key={suggestion.name}
                    suggestion={suggestion}
                    handleClick={this.handleSuggestionSelected}
                    handleItemEnter={this.handleMouseEnterItem}
                    handleItemLeave={this.handleMouseLeaveItem}
                    pageType={pageType}
                    enterPressed={this.state.enterPressed}
                />
            );
            offset += suggestion.contents.length;
            return element;
        });
        const footer = this.props.showFooter ? (
            <footer>
                <a onClick={this.handleFooterClick}>
                    View All Results for {this.state.userEnteredText}
                </a>
            </footer>
        ) : null;
        const suggestionsNode = this.state.displaySuggestions ? (
            <div className="search_results search_results--active">
                <div className="search_results__wrapper">
                    {suggestionElements}
                </div>
                {footer}
            </div>
        ) : null;

        return enabled ? (
            <section
                style={style}
                onMouseEnter={this.handleMouseEnter}
                onMouseLeave={this.handleMouseLeave}
                className={searchClasses}
            >
                <form onSubmit={this.handleSearchSubmit} >
                    <input
                        type="text"
                        autoComplete="off"
                        placeholder={formatMessage(messages['search.placeholder'])}
                        name="search-section"
                        value={this.state.searchTerm}
                        onChange={this.handleSearchTermChange}
                        onBlur={this.handleInputOnBlur}
                    />
                </form>
                {suggestionsNode}
            </section>
        ) : null;
    }
}

SearchComponent.propTypes = {
    dispatch: PropTypes.func.isRequired,
    pageType: PropTypes.string,
    enabled: PropTypes.bool,
    router: PropTypes.object,
    intl: intlShape.isRequired,
    messages: PropTypes.object.isRequired,
    apiEnv: PropTypes.string,
    entitlementsTracking: PropTypes.object,
    typeAhead: PropTypes.array,
    showFooter: PropTypes.bool
};

SearchComponent.defaultProps = {
    enabled: false,
    typeAhead: [],
    showFooter: true
};

const mapStateToProps = (state) => ({
    apiEnv: apiEnvSelector(state),
    typeAhead: competitorSelector(state)
});

export default withRouter(injectIntl(connect(mapStateToProps)(SearchComponent)));



// WEBPACK FOOTER //
// ./app/common/search/SearchContainer.js